import { PaginatedResponse, useApi } from '@/api';
import { Asset, Link, TableCellBottom } from '@/components';
import { DEFAULT_NETWORK } from '@/constants';
import {
  CurrencyValue,
  DataTable,
  Flex,
  Heading,
  PercentageValue,
  TableSkeleton,
  useTableProps
} from '@blockanalitica/ui';
import { TableColumn } from '@blockanalitica/ui/package/types/src/components/molecules/DataTable';

type ParamsData = {
  underlying_address: string;
  underlying_name: string;
  underlying_symbol: string;
  ltv: string;
  liquidation_threshold: string;
  liquidation_bonus: string;
  supply_cap: string | null;
  supply_cap_usd: string;
  borrow_cap: string | null;
  borrow_cap_usd: string;
  reserve_factor: string;
};

export default function MarketParamsTable() {
  const network = DEFAULT_NETWORK;

  const { sorting } = useTableProps({
    pageSize: 15,
    initialSorting: '-ltv'
  });
  const [data, error] = useApi<PaginatedResponse<ParamsData[]>>({
    url: '/params/',
    queryParams: {
      sort: sorting.currentSorting
    }
  });

  if (error) {
    throw error;
  }

  if (!data) {
    return <TableSkeleton rows={10} columns={6} />;
  }

  const columns: TableColumn<ParamsData>[] = [
    {
      header: 'Market',
      getCell: (row) => (
        <Link to={`/markets/${row.underlying_address}`}>
          <Asset
            symbol={row.underlying_symbol}
            address={row.underlying_address}
            network={network}
            name={row.underlying_name}
            iconSize="3"
          />
        </Link>
      ),
      cellAlign: 'flex-start',
      headerAlign: 'flex-start'
    },
    {
      header: 'LTV',
      getCell: (row) => <PercentageValue value={row.ltv} />,
      sortingField: 'ltv',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Liquidation Threshold',
      getCell: (row) => <PercentageValue value={row.liquidation_threshold} />,
      sortingField: 'liquidation_threshold',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Liquidation Bonus',
      getCell: (row) => <PercentageValue value={row.liquidation_bonus} />,
      sortingField: 'liquidation_bonus',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Reserve Factor',
      getCell: (row) => <PercentageValue value={row.reserve_factor} />,
      sortingField: 'reserve_factor',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Supply Cap',
      getCell: (row) =>
        row.supply_cap != null ? (
          <CurrencyValue
            value={row.supply_cap}
            options={{
              currencySize: '-1',
              currency: {
                name: row.underlying_symbol,
                address: row.underlying_address,
                network: network
              }
            }}
          />
        ) : (
          '-'
        ),
      getCellBottom: (row) =>
        row.supply_cap != null ? (
          <TableCellBottom checkValue={row.supply_cap_usd}>
            <CurrencyValue
              value={row.supply_cap_usd}
              options={{
                currency: 'USD'
              }}
            />
          </TableCellBottom>
        ) : null,
      sortingField: 'supply_cap_usd',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    },
    {
      header: 'Borrow Cap',

      getCell: (row) =>
        row.borrow_cap != null ? (
          <CurrencyValue
            value={row.borrow_cap}
            options={{
              currencySize: '-1',
              currency: {
                name: row.underlying_symbol,
                address: row.underlying_address,
                network: network
              }
            }}
          />
        ) : (
          '-'
        ),
      getCellBottom: (row) =>
        row.borrow_cap != null ? (
          <TableCellBottom checkValue={row.borrow_cap_usd}>
            <CurrencyValue
              value={row.borrow_cap_usd}
              options={{
                currency: 'USD'
              }}
            />
          </TableCellBottom>
        ) : null,
      sortingField: 'borrow_cap_usd',
      cellAlign: 'flex-end',
      headerAlign: 'flex-end'
    }
  ];

  return (
    <Flex flexDirection="column" gap="s">
      <Heading size="3">Current Parameters</Heading>
      <DataTable
        rows={data['results']}
        columns={columns}
        getKey={(row) => row.underlying_address}
        sorting={sorting}
      />
    </Flex>
  );
}
